import { useApolloClient } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  DialogActions,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Modal, ModalOpenButton } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import {
  namedOperations,
  OfferedProjectType,
} from "../../../../clients/graphqlTypes.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import {
  useDeleteOrganisationCategoryProjectTypeMappingOverrideMutation,
  useEditOverridesModal_CategoryGlobalMappingsQuery,
  useEditOverridesModal_TemplatesByIdsQuery,
  useUpsertOrganisationCategoryProjectTypeMappingOverrideMutation,
  type CategoryProjectTypeMappingFieldsFragment,
} from "./EditOverridesModal.generated.js";
import type { OrganisationCategoryProjectTypeMappingOverrideFieldsFragment } from "../../../main-routes/my-organisation/OrganisationSettingsOfferedServices.generated.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import { TemplateSelectModal } from "../../templates/quote/TemplateSelectModal.js";
import type { TemplateBase } from "../../templates/quote/TemplatesDisplay.js";

const REFETCH = {
  refetchQueries: [namedOperations.Query.OrganisationSettingsOfferedServices],
  awaitRefetchQueries: true,
};

export type EditOverridesModalProps = {
  organisationId: string;
  categoryId: string;
  categoryName: string;
  projectType: OfferedProjectType;
  overrides: ReadonlyArray<OrganisationCategoryProjectTypeMappingOverrideFieldsFragment>;
  onClose: () => void;
};

export type OverrideRowSnapshot = {
  projectType: OfferedProjectType;
  initialOverride: OrganisationCategoryProjectTypeMappingOverrideFieldsFragment | null;
};

type RowDraft = OverrideRowSnapshot & {
  draftTemplateId: string | null;
  draftSmartProcess: string;
};

export const buildOverrideRowSnapshot = ({
  projectType,
  overrides,
}: {
  projectType: OfferedProjectType;
  overrides: ReadonlyArray<OrganisationCategoryProjectTypeMappingOverrideFieldsFragment>;
}): OverrideRowSnapshot => ({
  projectType,
  initialOverride: overrides.find(o => o.projectType === projectType) ?? null,
});

export type PendingOp = {
  kind: "upsert";
  input: {
    categoryId: string;
    projectType: OfferedProjectType;
    defaultTemplateId: string | null;
    defaultSmartProcess: string | null;
  };
};

export const computePendingOp = (
  categoryId: string,
  row: RowDraft
): PendingOp | null => {
  const draftTemplate = row.draftTemplateId;
  const draftSmart =
    row.draftSmartProcess.trim() === "" ? null : row.draftSmartProcess.trim();
  const initialTemplate = row.initialOverride?.defaultTemplateId ?? null;
  const initialSmart = row.initialOverride?.defaultSmartProcess ?? null;
  if (draftTemplate === initialTemplate && draftSmart === initialSmart) {
    return null;
  }
  return {
    kind: "upsert",
    input: {
      categoryId,
      projectType: row.projectType,
      defaultTemplateId: draftTemplate,
      defaultSmartProcess: draftSmart,
    },
  };
};

export const EditOverridesModal = ({
  organisationId,
  categoryId,
  categoryName,
  projectType,
  overrides,
  onClose,
}: EditOverridesModalProps) => {
  const { t } = useTranslate(["OrganisationProfile", "Global", "Templates"]);
  const client = useApolloClient();
  const { projectTypeLabels } = useProjectTypes();
  const { enqueueSnackbar } = useSnackbar();

  // The global (platform-wide) category→project-type defaults are only needed
  // while this modal is open, for the single category being edited — so they
  // are fetched here rather than eagerly through the offered-services list.
  const globalMappingsQuery = useEditOverridesModal_CategoryGlobalMappingsQuery(
    {
      client,
      variables: { categoryId },
    }
  );
  const globalMappings =
    globalMappingsQuery.data?.categories[0]?.projectTypeMappings ?? [];

  // Display-only and resolved live from the async fetch, so the "Global default"
  // column reflects loaded data even though `row` is seeded on first render
  // (only `initialOverride`, sourced from the synchronously-available `overrides`
  // prop, needs to seed editable state).
  const globalDefault =
    globalMappings.find(m => m.projectType === projectType) ?? null;

  const [row, setRow] = React.useState<RowDraft>(() => {
    // Only `initialOverride` (from the synchronously-available `overrides` prop)
    // seeds editable state; `globalDefault` is resolved live above for display.
    const snapshot = buildOverrideRowSnapshot({ projectType, overrides });
    return {
      ...snapshot,
      draftTemplateId: snapshot.initialOverride?.defaultTemplateId ?? null,
      draftSmartProcess: snapshot.initialOverride?.defaultSmartProcess ?? "",
    };
  });

  const [wasAddedByUser, setWasAddedByUser] = React.useState(false);

  const referencedTemplateIds = React.useMemo(() => {
    const ids = new Set<string>();
    if (globalDefault?.defaultTemplateId)
      ids.add(globalDefault.defaultTemplateId);
    if (row.draftTemplateId) ids.add(row.draftTemplateId);
    if (row.initialOverride?.defaultTemplateId)
      ids.add(row.initialOverride.defaultTemplateId);
    return Array.from(ids);
  }, [globalDefault, row]);

  const templatesQuery = useEditOverridesModal_TemplatesByIdsQuery({
    client,
    variables: { templateIds: referencedTemplateIds },
    // Wait for the global mappings to settle so the full set of referenced
    // template ids (including the global default's) is known on the first
    // fetch, rather than firing once now and refetching when they resolve.
    skip: globalMappingsQuery.loading || referencedTemplateIds.length === 0,
  });

  const templateTitleById = React.useMemo(() => {
    const map = new Map<string, string>();
    const conn = templatesQuery.data?.searchTemplates;
    if (conn?.__typename === "QuoteTemplateConnection") {
      for (const edge of conn.edges) map.set(edge.node.id, edge.node.title);
    }
    return map;
  }, [templatesQuery.data]);

  const [upsert] =
    useUpsertOrganisationCategoryProjectTypeMappingOverrideMutation({
      client,
    });
  const [del] = useDeleteOrganisationCategoryProjectTypeMappingOverrideMutation(
    {
      client,
    }
  );
  const [saving, setSaving] = React.useState(false);

  const pendingOp = React.useMemo(
    () => computePendingOp(categoryId, row),
    [categoryId, row]
  );

  const handleSave = async () => {
    if (!pendingOp) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await upsert({
        variables: {
          input: { organisationId, ...pendingOp.input },
        },
      });
      await client.refetchQueries({
        include: REFETCH.refetchQueries,
      });
      onClose();
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  // The footer-left "Remove override" button is only meaningful when the row
  // has something to remove on the server (an existing override).
  const removableRow = row.initialOverride ? row : null;

  // Errors are caught and surfaced by ConfirmModal itself (snackbar + Sentry +
  // modal stays open for retry), so we let them propagate.
  const handleRemoveConfirmed = async () => {
    if (!removableRow) return;
    setSaving(true);
    try {
      await del({
        variables: {
          input: {
            organisationId,
            categoryId,
            projectType: removableRow.projectType,
          },
        },
      });
      await client.refetchQueries({
        include: REFETCH.refetchQueries,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("Edit overrides for {category}", {
        ns: "OrganisationProfile",
        category: categoryName,
      })}
      maxWidth="md"
      handleClose={saving ? () => undefined : onClose}
      dialogActions={
        <DialogActions sx={{ justifyContent: "space-between" }}>
          {removableRow ? (
            <ModalOpenButton
              Modal={ConfirmModal}
              modalProps={{
                title: t("Remove override?", { ns: "OrganisationProfile" }),
                text: t(
                  "The global default will be used for this offered service.",
                  { ns: "OrganisationProfile" }
                ),
                confirmButtonLabel: t("Remove override", {
                  ns: "OrganisationProfile",
                }),
                confirmButtonProps: { color: "error" },
                handleConfirm: handleRemoveConfirmed,
                isLoading: saving,
              }}
            >
              <Button
                color="error"
                variant="text"
                startIcon={<DeleteOutlineIcon />}
                disabled={saving}
              >
                {t("Remove override", { ns: "OrganisationProfile" })}
              </Button>
            </ModalOpenButton>
          ) : (
            <span />
          )}
          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              onClick={onClose}
              disabled={saving}
              color="primary"
            >
              {t("Cancel", { ns: "Global" })}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              loading={saving}
              disabled={saving || !pendingOp}
            >
              {t("Save", { ns: "Global" })}
            </Button>
          </Stack>
        </DialogActions>
      }
    >
      <OverrideRow
        row={row}
        globalDefault={globalDefault}
        globalDefaultLoading={globalMappingsQuery.loading}
        globalDefaultError={Boolean(globalMappingsQuery.error)}
        label={projectTypeLabels[row.projectType]}
        templateTitleById={templateTitleById}
        isExpanded={row.initialOverride !== null || wasAddedByUser}
        onChangeTemplate={tid =>
          setRow(prev => ({ ...prev, draftTemplateId: tid }))
        }
        onChangeSmartProcess={v =>
          setRow(prev => ({ ...prev, draftSmartProcess: v }))
        }
        onAddOverride={() => setWasAddedByUser(true)}
        disabled={saving}
      />
    </Modal>
  );
};

type OverrideRowProps = {
  row: RowDraft;
  globalDefault: CategoryProjectTypeMappingFieldsFragment | null;
  globalDefaultLoading: boolean;
  globalDefaultError: boolean;
  label: string;
  templateTitleById: ReadonlyMap<string, string>;
  isExpanded: boolean;
  onChangeTemplate: (templateId: string | null) => void;
  onChangeSmartProcess: (value: string) => void;
  onAddOverride: () => void;
  disabled: boolean;
};

const OverrideRow = ({
  row,
  globalDefault,
  globalDefaultLoading,
  globalDefaultError,
  label,
  templateTitleById,
  isExpanded,
  onChangeTemplate,
  onChangeSmartProcess,
  onAddOverride,
  disabled,
}: OverrideRowProps) => {
  const { t } = useTranslate(["OrganisationProfile", "Global", "Templates"]);

  const globalTemplateTitle = globalDefault?.defaultTemplateId
    ? (templateTitleById.get(globalDefault.defaultTemplateId) ??
      globalDefault.defaultTemplateId)
    : null;

  const overrideTemplateTitle = row.draftTemplateId
    ? (templateTitleById.get(row.draftTemplateId) ?? row.draftTemplateId)
    : null;

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Typography variant="h4">{label}</Typography>
        {!isExpanded && (
          <Button
            size="small"
            variant="text"
            startIcon={<AddIcon />}
            onClick={onAddOverride}
            disabled={disabled}
          >
            {t("Add override", { ns: "OrganisationProfile" })}
          </Button>
        )}
      </Stack>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="stretch"
      >
        <Stack spacing={0.5} flex={1}>
          <Typography variant="caption" color="text.secondary">
            {t("Global default", { ns: "OrganisationProfile" })}
          </Typography>
          {globalDefaultLoading ? (
            <Typography variant="body2" color="text.secondary">
              {t("Loading…", { ns: "Global" })}
            </Typography>
          ) : globalDefaultError ? (
            <Typography variant="body2" color="error">
              {t("Error", { ns: "Global" })}
            </Typography>
          ) : (
            <>
              <Typography variant="body2">
                {t("Template", { ns: "OrganisationProfile" })}
                {": "}
                {globalTemplateTitle ?? "—"}
              </Typography>
              <Typography variant="body2">
                {t("Smart process", { ns: "OrganisationProfile" })}
                {": "}
                {globalDefault?.defaultSmartProcess ?? "—"}
              </Typography>
            </>
          )}
        </Stack>
        {isExpanded && (
          <Stack spacing={1} flex={1}>
            <Typography variant="caption" color="text.secondary">
              {t("Override", { ns: "OrganisationProfile" })}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box flex={1}>
                <Typography variant="body2">
                  {t("Template", { ns: "OrganisationProfile" })}
                  {": "}
                  {overrideTemplateTitle ?? "—"}
                </Typography>
              </Box>
              <ModalOpenButton
                Modal={TemplateSelectModal}
                modalProps={{
                  title: t("Select template", { ns: "Templates" }),
                  initialFilters: {},
                  initialSorting: [],
                  handleComplete: (template: TemplateBase, close) => {
                    onChangeTemplate(template.id);
                    close();
                  },
                }}
              >
                <Button variant="outlined" size="small" disabled={disabled}>
                  {t("Choose…", { ns: "Global" })}
                </Button>
              </ModalOpenButton>
              {row.draftTemplateId !== null && (
                <Button
                  variant="text"
                  size="small"
                  disabled={disabled}
                  onClick={() => onChangeTemplate(null)}
                >
                  {t("Clear", { ns: "Global" })}
                </Button>
              )}
            </Stack>
            <TextField
              size="small"
              label={t("Smart process", { ns: "OrganisationProfile" })}
              value={row.draftSmartProcess}
              disabled={disabled}
              onChange={e => onChangeSmartProcess(e.target.value)}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
